import { useEffect, useId, useMemo, useRef, useState } from "react";
import { TextField } from "@/components/TextField";
import { SearchSelect } from "@/components/SearchSelect";
import { US_STATE_OPTIONS } from "@/lib/usStates";
import {
  editAddressPart,
  withoutCounty,
  type AddressValue,
  type CountySource,
} from "@/lib/address";
import {
  defaultPlacesClient,
  PlacesSession,
  MIN_QUERY_LENGTH,
  DEBOUNCE_MS,
  type PlaceSuggestion,
  type PlacesClient,
} from "@/lib/placesClient";
import { FIELD_ERROR } from "@/components/fieldStyles";
import { TOUCH_TARGET } from "@/components/buttonStyles";
import { LoadingNote } from "@/components/LoadingNote";

// The ONE shared US-address input (BRK-S067) — street + city + state (2-letter
// USPS select) + ZIP. Extracted from the blocks inlined in AccountForm (legal +
// mailing) and reused by BrokerageCard, so the field set, required markers, and
// state dropdown live in one place (DRY). Controlled: the parent owns the value
// and every field stays editable.
//
// When a Google Places client is available (a browser key is configured, or the
// dev-mock path is active), the STREET field becomes a headless autocomplete —
// suggestions in our own dropdown (matching BRK's typeahead idiom), never
// Google's embedded widget. With no client it is exactly today's manual field
// (no autocomplete, no errors, no console noise). See the shared UX contract:
// pms-local docs/address-autocomplete.md.
//
// The county (BRK-S308) is opt-in. The commercial account addresses ask for it
// (`withCounty`): Places fills it, the broker can type it, and a county that
// came with the address is cleared when the street, city or state is edited
// away by hand. The bond and surety editors don't opt in, so their values keep
// exactly four parts — a Places fill there carries no county either.
//
// Line 2 (BRK-S302) is opt-in the same way: the WC wizard's locations ask for
// it (`withLine2`), and Places fills it from the unit (subpremise).

export function AddressFieldset({
  title,
  value,
  onChange,
  bare = false,
  placesClient,
  error,
  groupLabel,
  withCounty = false,
  countyRequired = false,
  withLine2 = false,
}: {
  /** Optional heading rendered above the fields (omit for a bare, headingless
   *  block embedded in a larger form, e.g. BrokerageCard). */
  title?: string;
  value: AddressValue;
  onChange: (a: AddressValue) => void;
  /** Drop the card chrome (border/background/padding) — for an address nested
   *  inside another card. */
  bare?: boolean;
  /** Places client override. `undefined` ⇒ resolve the default from config;
   *  `null` ⇒ force the manual-only field. Primarily a test seam. */
  placesClient?: PlacesClient | null;
  /**
   * A validation message for the address AS A WHOLE (BRK-S102). An address is
   * four inputs under one label, so a server reason about "the address" belongs
   * to the GROUP — announced to anyone inside it — rather than being pinned on
   * an arbitrary member like `street`.
   */
  error?: string;
  /** Accessible name for the group, when `title` isn't rendered (bare mode).
   *  Needed so the described group has a name to be announced with. */
  groupLabel?: string;
  /** Ask for the county too (BRK-S308): the commercial account addresses. */
  withCounty?: boolean;
  /** The county is on the stage floor (a WC quote needs the rated
   *  location's county). Optional otherwise; PMS's floor is the authority. */
  countyRequired?: boolean;
  /** Ask for line 2 too, a unit or suite (BRK-S302): the WC locations. */
  withLine2?: boolean;
}) {
  const groupId = useId();
  // A county that arrived with the address (stored, or filled by Places)
  // belongs to it; one the broker typed is theirs (see editAddressPart).
  const [countySource, setCountySource] = useState<CountySource>("auto");
  const edit = (part: "street" | "city" | "state" | "zip", next: string) =>
    onChange(
      withCounty
        ? editAddressPart(value, part, next, countySource)
        : { ...value, [part]: next },
    );
  // A fill is a new address: it keeps the optional parts this editor asks
  // for, and only those.
  const fill = (place: AddressValue) => {
    setCountySource("auto");
    const { county, addressLine2 } = place;
    onChange({
      ...withoutCounty(place),
      ...(withLine2 && addressLine2 ? { addressLine2 } : {}),
      ...(withCounty && county ? { county } : {}),
    });
  };
  const client = useMemo(
    () => (placesClient !== undefined ? placesClient : defaultPlacesClient()),
    [placesClient],
  );

  const fields = (
    <>
      {title && <h2 className="text-heading">{title}</h2>}
      {client ? (
        <AutocompleteStreet
          street={value.street}
          onStreetChange={(next) => edit("street", next)}
          onFill={fill}
          client={client}
        />
      ) : (
        <TextField
          label="Street"
          value={value.street}
          onChange={(e) => edit("street", e.target.value)}
          required
        />
      )}
      {withLine2 && (
        <TextField
          label="Address line 2"
          value={value.addressLine2 ?? ""}
          onChange={(e) => onChange({ ...value, addressLine2: e.target.value })}
          optional
        />
      )}
      {/* Four parts sit two by two; three stay in one row, as they always have. */}
      <div
        className={`grid grid-cols-1 gap-3 ${withCounty ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}
      >
        <TextField
          label="City"
          value={value.city}
          onChange={(e) => edit("city", e.target.value)}
          required
        />
        {withCounty && (
          <TextField
            label="County"
            value={value.county ?? ""}
            onChange={(e) => {
              setCountySource("typed");
              onChange({ ...value, county: e.target.value });
            }}
            required={countyRequired}
            optional={!countyRequired}
          />
        )}
        <SearchSelect
          label="State"
          placeholder="Select"
          options={US_STATE_OPTIONS}
          value={value.state}
          onChange={(state) => edit("state", state)}
          required
        />
        <TextField
          label="ZIP"
          value={value.zip}
          onChange={(e) => edit("zip", e.target.value)}
          required
        />
      </div>
    </>
  );
  // An address error describes the whole block, so it rides on a named group and
  // is wired with aria-describedby — visible-but-unannounced is the failure mode
  // BRK-S102 was filed for.
  const errorId = error ? `${groupId}-address-error` : undefined;
  const groupProps = {
    role: "group" as const,
    "aria-label": groupLabel ?? title,
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": errorId,
  };
  const message = error ? (
    <p id={errorId} className={FIELD_ERROR}>
      {error}
    </p>
  ) : null;
  if (bare)
    return (
      <div className="flex flex-col gap-4" {...groupProps}>
        {fields}
        {message}
      </div>
    );
  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5"
      {...groupProps}
    >
      {fields}
      {message}
    </div>
  );
}

// The street field with Places autocomplete. Renders the shared TextField plus
// an in-flow suggestion dropdown (in-flow, like ClassCodeField, so it never
// overflows at 375px). Mounted only when a client exists.
function AutocompleteStreet({
  street,
  onStreetChange,
  onFill,
  client,
}: {
  street: string;
  onStreetChange: (next: string) => void;
  /** A resolved place: the parent decides which parts it keeps. */
  onFill: (place: AddressValue) => void;
  client: PlacesClient;
}) {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[] | null>(
    null,
  );
  // "Enter address manually" collapses autocomplete ENTIRELY for the life of
  // the field — no more Places requests (shared contract).
  const [manual, setManual] = useState(false);
  // Skip one query right after a programmatic fill (a selection sets the full
  // street), so choosing a suggestion doesn't immediately re-query it.
  const [suppress, setSuppress] = useState(false);
  // The current billing session — one token spans this focus-session and is
  // consumed by the Place Details call; discarded after a selection so the next
  // focus-session mints a fresh token.
  const sessionRef = useRef<PlacesSession | null>(null);

  // Debounced suggestion fetch: only while focused, not in manual mode, at or
  // above the min length, and not suppressed by a just-completed selection.
  useEffect(() => {
    if (manual || !focused || suppress) return;
    const q = street.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setSuggestions(null);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      // Created on the first qualifying keystroke; reused for the whole session.
      sessionRef.current ??= new PlacesSession();
      try {
        const items = await sessionRef.current.suggest(client, q, ctrl.signal);
        if (!cancelled) {
          setSuggestions(items);
          setOpen(true);
        }
      } catch {
        // Handled state, never a throw or console noise: an empty dropdown.
        if (!cancelled) {
          setSuggestions([]);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [street, focused, manual, suppress, client]);

  async function selectSuggestion(s: PlaceSuggestion) {
    const session = sessionRef.current ?? new PlacesSession();
    setOpen(false);
    setLoading(true);
    setSuppress(true); // the fill sets value.street — don't re-query it
    try {
      const addr = await session.finalize(client, s.placeId);
      onFill(addr); // fills every part; every field stays editable
    } catch {
      // Details failed — keep the typed suggestion text; still no throw.
      onStreetChange(s.primaryText);
    } finally {
      setLoading(false);
      sessionRef.current = null; // session consumed → next one is fresh
      setSuggestions(null);
    }
  }

  function handleStreetChange(next: string) {
    setSuppress(false); // user is typing again → allow queries
    onStreetChange(next);
  }

  function enterManually() {
    setManual(true);
    setOpen(false);
    setSuggestions(null);
    sessionRef.current = null;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <TextField
        label="Street"
        value={street}
        onChange={(e) => handleStreetChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          // Delay the close so a suggestion click (which we keep focus for via
          // onMouseDown) still registers before the dropdown unmounts.
          window.setTimeout(() => setOpen(false), 150);
        }}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        required
      />
      {/* A separate call says why it waits, with a spinner, from the first
          lookup on (BRK-S488). */}
      {!manual && loading && (
        <LoadingNote
          label="Looking up the address…"
          className="px-1 text-xs text-brk-dim"
        />
      )}
      {!manual && open && (
        <div className="flex flex-col gap-1">
          {suggestions && suggestions.length > 0 && (
            <ul
              role="listbox"
              aria-label="Address suggestions"
              className="flex flex-col overflow-hidden rounded-md border border-brk-border"
            >
              {suggestions.map((s) => (
                <li
                  key={s.placeId}
                  className="border-t border-brk-border first:border-t-0"
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    // Keep focus so onBlur doesn't close the list before onClick.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void selectSuggestion(s)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-brk-bg"
                  >
                    <span className="text-brk-text">{s.primaryText}</span>
                    {s.secondaryText && (
                      <span className="text-xs text-brk-dim">
                        {s.secondaryText}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions && suggestions.length === 0 && !loading && (
            <p className="px-1 text-xs text-brk-dim">
              No matches — keep typing, or enter it manually below.
            </p>
          )}
        </div>
      )}
      {!manual && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={enterManually}
          // Muted, not accent (BRK-S091): the brand accent is red, so an
          // accent-coloured line sitting under an address input read as a
          // validation message rather than the secondary action it is.
          className={`self-start text-xs text-brk-dim underline decoration-dotted hover:text-brk-text ${TOUCH_TARGET}`}
        >
          Enter address manually
        </button>
      )}
    </div>
  );
}
